/**
 * Fountains API - Cliente HTTP para gestión de fuentes y bebederos
 *
 * @version 1.0
 * @updated 2026-02-27
 * @module core/map-tools
 */

import axios from 'axios';
import type { FountainComplete, Coordinates, FountainType, FountainStatus } from './fountain-types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

/**
 * Obtiene el token de autenticación del localStorage
 */
const getAuthToken = (): string | null => {
  const authStorage = localStorage.getItem('auth-storage');
  if (!authStorage) return null;
  const parsed = JSON.parse(authStorage) as { state?: { token?: string } };
  return parsed.state?.token || null;
};

/**
 * DTO para crear una fuente
 */
export interface CreateFountainDto {
  projectId: string;
  coordinates: Coordinates;
  type: FountainType;
  code?: string;
  data?: Record<string, any>;
}

/**
 * DTO para actualizar una fuente
 */
export interface UpdateFountainDto {
  code?: string;
  coordinates?: Coordinates;
  type?: FountainType;
  data?: Record<string, any>;
  status?: FountainStatus;
}

/**
 * Respuesta del backend para una fuente
 */
export interface FountainResponse {
  id: string;
  project_id: string;
  code: string;
  coordinates: Coordinates;
  type: FountainType;
  data: Record<string, any>;
  status: FountainStatus;
  created_by: string;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Cliente API para fuentes
 */
export const fountainsApi = {
  /**
   * Crear una nueva fuente
   *
   * @param fountain - Datos de la fuente (requiere projectId, coordenadas y tipo)
   * @returns Fuente creada
   *
   * @example
   * ```typescript
   * const newFountain = await fountainsApi.create({
   *   projectId: 'uuid-del-proyecto',
   *   coordinates: [-68.5375, -31.5383],
   *   type: 'drinking'
   * });
   * ```
   */
  async create(fountain: CreateFountainDto): Promise<FountainResponse> {
    const token = getAuthToken();
    const response = await axios.post<FountainResponse>(
      `${API_URL}/fountains`,
      fountain,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data;
  },

  /**
   * Obtener todas las fuentes de un proyecto
   *
   * @param projectId - ID del proyecto
   * @returns Lista de fuentes
   */
  async getByProject(projectId: string): Promise<FountainResponse[]> {
    const token = getAuthToken();
    const response = await axios.get<FountainResponse[]>(
      `${API_URL}/fountains/project/${projectId}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data;
  },

  /**
   * Obtener una fuente por ID
   *
   * @param id - ID de la fuente
   * @returns Fuente encontrada
   */
  async get(id: string): Promise<FountainResponse> {
    const token = getAuthToken();
    const response = await axios.get<FountainResponse>(
      `${API_URL}/fountains/${id}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data;
  },

  /**
   * Actualizar una fuente
   *
   * @param id - ID de la fuente
   * @param updates - Campos a actualizar
   * @returns Fuente actualizada
   */
  async update(id: string, updates: UpdateFountainDto): Promise<FountainResponse> {
    const token = getAuthToken();
    const response = await axios.put<FountainResponse>(
      `${API_URL}/fountains/${id}`,
      updates,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data;
  },

  /**
   * Eliminar una fuente (soft delete)
   *
   * @param id - ID de la fuente
   * @returns Fuente eliminada
   */
  async delete(id: string): Promise<FountainResponse> {
    const token = getAuthToken();
    const response = await axios.delete<FountainResponse>(
      `${API_URL}/fountains/${id}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data;
  },
};

/**
 * Convierte FountainComplete (frontend) a CreateFountainDto (backend)
 *
 * @param fountain - Fuente del frontend
 * @param projectId - ID del proyecto
 * @returns DTO para el backend
 *
 * @remarks
 * Si la fuente tiene un código temporal (TEMP-FOUNTAIN-*), no se envía al backend
 * para permitir que el sistema de codificación geoespacial genere el código
 * definitivo basado en la ubicación.
 */
export function fountainToCreateDto(fountain: FountainComplete, projectId: string): CreateFountainDto {
  // No enviar códigos temporales al backend
  const isTemporaryCode = fountain.code?.startsWith('TEMP-') ?? false;

  return {
    projectId,
    coordinates: fountain.coordinates,
    type: fountain.type,
    code: isTemporaryCode ? undefined : fountain.code,
    data: {
      // General
      name: fountain.name,
      address: fountain.address,
      neighborhood: fountain.neighborhood,
      locationType: fountain.locationType,
      installationDate: fountain.installationDate?.toISOString(),
      material: fountain.material,
      style: fountain.style,

      // Operational
      status: fountain.status,
      waterQuality: fountain.waterQuality,
      lastQualityTest: fountain.lastQualityTest?.toISOString(),
      flowRate: fountain.flowRate,
      hasFilter: fountain.hasFilter,
      filterType: fountain.filterType,
      lastFilterChange: fountain.lastFilterChange?.toISOString(),
      hasDrainage: fountain.hasDrainage,
      drainageCondition: fountain.drainageCondition,
      surroundingCondition: fountain.surroundingCondition,
      hasLighting: fountain.hasLighting,

      // Maintenance
      lastMaintenance: fountain.lastMaintenance?.toISOString(),
      maintenanceType: fountain.maintenanceType,
      maintenanceNotes: fountain.maintenanceNotes,
      nextScheduledMaintenance: fountain.nextScheduledMaintenance?.toISOString(),
      assignedResponsible: fountain.assignedResponsible,
      needsIntervention: fountain.needsIntervention,
      interventionType: fountain.interventionType,
      priority: fountain.priority,

      // Accessibility & Heritage
      accessibilityLevel: fountain.accessibilityLevel,
      hasWheelchairAccess: fountain.hasWheelchairAccess,
      hasPetBowl: fountain.hasPetBowl,
      childFriendly: fountain.childFriendly,
      hasHistoricalValue: fountain.hasHistoricalValue,
      historicalDescription: fountain.historicalDescription,
      isProtected: fountain.isProtected,
      protectionLaw: fountain.protectionLaw,
      owner: fountain.owner,
      managementArea: fountain.managementArea,
      generalNotes: fountain.generalNotes,
    },
  };
}
